import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';

function isDesktopRuntime() {
  if (typeof window === 'undefined') {
    return false;
  }

  return Boolean(window.__CAFE_DESKTOP_RUNTIME__);
}

function isAllowedLocalAppOrigin() {
  if (typeof window === 'undefined') {
    return false;
  }

  const hostname = window.location.hostname || '';

  return (
    hostname === 'localhost'
    || hostname === '127.0.0.1'
    || hostname === '0.0.0.0'
    || hostname === '::1'
    || /^10\./.test(hostname)
    || /^192\.168\./.test(hostname)
    || /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname)
  );
}

function hasDesktopSession() {
  return Boolean(getDesktopSessionUser()?.id);
}

function getDesktopSessionUser() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem('cafe-desktop-session');
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as { user?: { id?: string; role?: string } | null } | null;
    return parsed?.user?.id ? parsed.user : null;
  } catch (error) {
    console.warn('Failed to inspect desktop session state', error);
    return null;
  }
}

function hasDesktopRole(roles: string[]) {
  const user = getDesktopSessionUser();
  return Boolean(user?.role && roles.includes(user.role));
}

function requireLocalSurface(roles?: string[]) {
  if (!isDesktopRuntime() && !isAllowedLocalAppOrigin()) {
    return '/';
  }

  if (isDesktopRuntime() && !hasDesktopSession()) {
    return '/desktop-entry';
  }

  if (isDesktopRuntime() && roles?.length && !hasDesktopRole(roles)) {
    return '/desktop-entry';
  }

  return true;
}

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'portal',
    component: () => import('../components/DesktopPortalShell.vue'),
    meta: {
      hideTopNav: true,
    },
  },
  {
    path: '/desktop-entry',
    alias: '/login',
    component: () => import('../views/DesktopWelcomeView.vue'),
    meta: {
      hideTopNav: true,
    },
  },
  {
    path: '/table',
    beforeEnter: () => requireLocalSurface(['admin', 'waiter']),
    component: () => import('../../apps/table/TablePage.vue'),
    meta: {
      hideTopNav: true,
    },
  },
  {
    path: '/table/:tableId',
    beforeEnter: () => requireLocalSurface(),
    component: () => import('../../apps/table/TablePage.vue'),
    meta: {
      hideTopNav: true,
    },
  },
  {
    path: '/storefront',
    beforeEnter: () => requireLocalSurface(),
    component: () => import('../../apps/desktop/StorefrontDisplayPage.vue'),
    meta: {
      hideTopNav: true,
    },
  },
  {
    path: '/admin/statistics',
    beforeEnter: () => requireLocalSurface(['admin']),
    component: () => import('../../apps/admin/AdminSalesStatisticsPage.vue'),
  },
  {
    path: '/admin',
    beforeEnter: () => requireLocalSurface(['admin']),
    component: () => import('../../apps/admin/AdminPage.vue'),
  },
  {
    // Подписка «Cafe Local Pro»: статус, оплата через Stripe, кабинет и история.
    path: '/admin/subscription',
    alias: '/extensions',
    beforeEnter: () => requireLocalSurface(['admin']),
    component: () => import('../../apps/admin/SubscriptionPage.vue'),
  },
  {
    path: '/portal-owner',
    beforeEnter: () => requireLocalSurface(['admin']),
    component: () => import('../../apps/portal-owner/PortalOwnerAdminPage.vue'),
    meta: {
      hideTopNav: true,
    },
  },
  {
    path: '/kitchen',
    beforeEnter: () => requireLocalSurface(['admin', 'kitchen']),
    component: () => import('../../apps/kitchen/KitchenPage.vue'),
  },
  {
    path: '/privacy',
    component: () => import('../views/PrivacyPolicyView.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/terms',
    component: () => import('../views/TermsView.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/cookies',
    component: () => import('../views/CookiePolicyView.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/geolocation',
    component: () => import('../views/GeolocationConsentView.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/venue-rules',
    component: () => import('../views/VenueRulesView.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/account-deletion',
    component: () => import('../views/AccountDeletionView.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/:catchAll(.*)',
    redirect: '/',
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.path === '/admin' || to.path === '/admin/statistics') {
      return { top: 0, left: 0 };
    }

    if (savedPosition) {
      return savedPosition;
    }

    return { top: 0, left: 0 };
  },
});

export default router;
